/**
 * Date helpers shared by the event sidebar and the quick-create dialog.
 *
 * The API stores one ISO instant per field, but cc-ui splits entry across a
 * CCDateField (`YYYY-MM-DD`) and a CCField `type="time"` (`HH:mm`), so both
 * screens need the same split-and-rejoin. They live here rather than in either
 * component so the two cannot drift apart.
 */

const pad = (n: number) => String(n).padStart(2, "0");

/** ISO instant to the local `YYYY-MM-DD` CCDateField expects. */
export function toDatePart(iso: string | undefined): string {
	if (!iso) return "";
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return "";
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** ISO instant to a local `HH:mm`. */
export function toTimePart(iso: string | undefined): string {
	if (!iso) return "";
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return "";
	return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * A Date to a local `YYYY-MM-DDTHH:mm:ss` string, with NO trailing `Z`.
 *
 * For the calendar's selected date, `toISOString()` is wrong: Sydney is UTC+10
 * or +11, so local midnight on 1 August serialises as `2026-07-31T14:00:00Z`,
 * and a date-only consumer then reads 31 July. Keeping it local-naive means
 * the day the user picked is the day every consumer reads back.
 */
export function toLocalIsoString(date: Date): string {
	if (Number.isNaN(date.getTime())) return "";
	return (
		`${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
		`T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
	);
}

/**
 * Date and time parts back to an ISO string the API accepts.
 *
 * An empty `time` yields midnight, which is what a date-only input means — so
 * turning the time toggle off and editing a date clears the time rather than
 * silently keeping a stale one.
 */
export function toIso(date: string, time: string): string {
	if (!date) return "";
	const d = new Date(`${date}T${time || "00:00"}`);
	return Number.isNaN(d.getTime()) ? "" : d.toISOString();
}

/**
 * The day heading the List view groups under, e.g. "Friday 14 August 2026".
 *
 * `en-AU`, not the browser default: the school reads 14 August, and an
 * en-US default would render the same instant as "August 14". The parts are
 * spelled out rather than using `toLocaleDateString`'s `dateStyle: "full"`,
 * which appends a comma after the weekday in every locale that has one.
 */
export function formatDayHeading(date: Date): string {
	if (Number.isNaN(date.getTime())) return "";
	return date.toLocaleDateString("en-AU", {
		weekday: "long",
		day: "numeric",
		month: "long",
		year: "numeric",
	});
}

/** The short date on a card, e.g. "Fri 14 Aug 2026". */
export function formatCardDate(date: Date): string {
	if (Number.isNaN(date.getTime())) return "";
	return date.toLocaleDateString("en-AU", {
		weekday: "short",
		day: "numeric",
		month: "short",
		year: "numeric",
	});
}

/** A local `YYYY-MM-DD` key, for grouping events into days. */
export function toDayKey(date: Date): string {
	if (Number.isNaN(date.getTime())) return "";
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

const timeOfDay = (date: Date): string =>
	date
		.toLocaleTimeString("en-AU", {
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		})
		// en-AU renders "8:00 am" on some engines and "8:00 AM" on others.
		// Lower-cased so a list never shows both spellings down one column.
		.toLowerCase();

/**
 * The time a row or card shows, e.g. "8:00 am – 4:00 pm".
 *
 * An en dash with hair spaces either side is what the design uses, and it is
 * also the correct mark for a range. A missing or unparseable end collapses to
 * the start alone rather than rendering a dangling dash, which is what the
 * Edumate feed's date-only events need: they carry a start and nothing else.
 */
export function formatTimeRange(
	start: string | undefined,
	end: string | undefined,
): string {
	if (!start) return "";
	const from = new Date(start);
	if (Number.isNaN(from.getTime())) return "";

	const to = end ? new Date(end) : null;
	if (!to || Number.isNaN(to.getTime())) return timeOfDay(from);
	if (to.getTime() === from.getTime()) return timeOfDay(from);
	return `${timeOfDay(from)} – ${timeOfDay(to)}`;
}

/**
 * A bump time as the API stores it: `HH:MM` and NOTHING ELSE.
 *
 * Not a date, and therefore not an instant and not a timezone — "call at four",
 * read against the day the event runs. The API refuses a full instant outright
 * rather than truncating one, deliberately, so that a client still sending the
 * old date-carrying value fails loudly instead of quietly losing the date.
 * `HH:MM:SS` is accepted because a time input with a `step` sends it; the
 * seconds are dropped, since no crew call is set to the second.
 */
const TIME_OF_DAY = /^(\d{1,2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/;

/** `HH:MM` to its parts, or `undefined` for anything that is not one. */
function parseTimeOfDay(
	value: string | null | undefined,
): { hour: number; minute: number } | undefined {
	const match = TIME_OF_DAY.exec((value ?? "").trim());
	if (!match) return undefined;
	const hour = Number(match[1]);
	const minute = Number(match[2]);
	if (hour > 23 || minute > 59) return undefined;
	return { hour, minute };
}

/** A stored `HH:MM` for reading, e.g. "6:00 am". "" for anything unparseable. */
export function formatTimeOfDay(value: string | null | undefined): string {
	const parsed = parseTimeOfDay(value);
	if (!parsed) return "";
	const on = new Date();
	on.setHours(parsed.hour, parsed.minute, 0, 0);
	return timeOfDay(on);
}

/**
 * Minutes between an event's edge and its bump time, for Mobiscroll's
 * `bufferBefore` / `bufferAfter`.
 *
 * A BUMP TIME IS A TIME OF DAY AND A BUFFER IS A DURATION, which is the whole
 * reason this exists. `BumpIn` is "06:00" with no day attached; `bufferBefore`
 * is a number of minutes hanging off the start. The event's own start and end
 * are untouched — the band is drawn outside them, which is where a bump time
 * sits.
 *
 * THE DAY IS THE NEAREST ONE, and that is the one real decision here. A bare
 * time cannot say which day it means, which is exactly why the API stopped
 * checking bump times against the event's own start and end: `01:00` against a
 * concert ending at 22:00 is an overnight pack-down, the commonest shape there
 * is, and refusing it would make that booking unrecordable. But a band has to
 * be drawn somewhere, so this reads each time as its NEAREST occurrence
 * OUTSIDE the event: the last `HH:MM` at or before the start, the next one at
 * or after the end. "Call at four" for an evening event is four that
 * afternoon, not four the previous night, and pack-down at one is one the
 * following morning.
 *
 * That rule bounds the band at a day, and it is a reading rather than a fact —
 * a bump in of 22:00 against a 09:00 start draws an eleven-hour band the
 * previous evening, which is right for a hall held overnight and merely large
 * for a mistyped field. Drawing nothing would hide both.
 *
 * `undefined` means "no band": either value missing, a bump time that is not a
 * wall-clock time, or a time that falls exactly on the edge — a band of no
 * height, for which Mobiscroll would still reserve the corner radius.
 */
export function bumpBufferMinutes(
	edge: string | undefined,
	bump: string | null | undefined,
	which: "before" | "after",
): number | undefined {
	if (!edge) return undefined;
	const parsed = parseTimeOfDay(bump);
	if (!parsed) return undefined;
	const at = new Date(edge);
	if (Number.isNaN(at.getTime())) return undefined;

	const on = new Date(at);
	on.setHours(parsed.hour, parsed.minute, 0, 0);
	const gap = () =>
		which === "before" ? at.getTime() - on.getTime() : on.getTime() - at.getTime();

	/* Rolled by a DAY rather than by 1,440 minutes. The two differ twice a year:
	   across a daylight-saving change the same wall-clock time the day before is
	   23 or 25 hours away, and adding a fixed number of minutes would put the
	   band an hour out on exactly the long-weekend events most likely to have a
	   bump in at all. */
	if (gap() < 0) on.setDate(on.getDate() + (which === "before" ? -1 : 1));

	const minutes = Math.round(gap() / 60000);
	return minutes > 0 ? minutes : undefined;
}
